import React, { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Button, Form, Alert, Row, Col, InputGroup } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { CREATE_CHECKLIST } from 'actions/formActions';

// React Icons
import {
    FaPlug,
    FaBolt,
    FaBoxes,
    FaProjectDiagram,
    FaChargingStation,
    FaExchangeAlt,
    FaThLarge
} from 'react-icons/fa';
import { GiElectricalResistance, GiElectric } from 'react-icons/gi';
import { BsBoxSeam } from 'react-icons/bs';

// Checklist Items
const checklistItems = [
    'APFC Panel',
    'Cabel Tray',
    'DB Panel',
    'Earthing',
    'HT Cabel',
    'Lightning Arrester',
    'LT Cabel',
    'LT Panel',
    'Transformer',
    'VCB',
    'HOTO (Project Handover Takeover Report)',
];

// Icon mapping
const checklistIcons = {
    'APFC Panel': <FaPlug size={24} />,
    'Cabel Tray': <BsBoxSeam size={24} />,
    'DB Panel': <FaThLarge size={24} />,
    'Earthing': <GiElectricalResistance size={24} />,
    'HT Cabel': <GiElectric size={24} />,
    'Lightning Arrester': <GiElectric size={24} />,
    'LT Cabel': <GiElectric size={24} />,
    'LT Panel': <FaBolt size={24} />,
    'Transformer': <FaExchangeAlt size={24} />,
    'VCB': <FaChargingStation size={24} />,
    'HOTO (Project Handover Takeover Report)': <FaProjectDiagram size={24} />,
};

const FormsChecklist = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [stationId, setStationId] = useState('');
    const [stationName, setStationName] = useState('');
    const [quantities, setQuantities] = useState(
        checklistItems.reduce((acc, item) => ({ ...acc, [item]: 0 }), {})
    );
    const [errors, setErrors] = useState({ stationId: '', checklist: '', stationName: '' });

    const handleQuantityChange = (item, value) => {
        const qty = parseInt(value, 10) || 0;
        setQuantities((prev) => ({ ...prev, [item]: qty }));
        if (errors.checklist) setErrors((prev) => ({ ...prev, checklist: '' }));
    };

    const adjustQuantity = (item, delta) => {
        setQuantities((prev) => {
            const updated = Math.max(0, (prev[item] || 0) + delta);
            return { ...prev, [item]: updated };
        });
    };

    const validate = () => {
        const validStationId = /^[A-Z0-9]+$/.test(stationId);
        const validStationName = stationName.trim() !== '';
        const totalSelected = Object.values(quantities).reduce((sum, qty) => sum + (qty > 0 ? 1 : 0), 0);

        const newErrors = {
            stationId: validStationId ? '' : 'Station ID must contain only letters and numbers.',
            stationName: validStationName ? '' : 'Station Name / Project Name is required.',
            checklist: totalSelected > 0 ? '' : 'Select at least one item with quantity greater than 0.',
        };

        setErrors(newErrors);
        return validStationId && validStationName && totalSelected > 0;
    };

    const handleSubmit = useCallback(() => {
        if (!validate()) return;

        const checklist = {};
        Object.entries(quantities).forEach(([item, quantity]) => {
            if (quantity > 0) {
                const key = item
                    .toLowerCase()
                    .replace(/\(.*?\)/g, '')
                    .replace(/[^a-z0-9]+/g, '_')
                    .replace(/^_|_$/g, '');
                checklist[key] = quantity;
            }
        });

        const payload = {
            stationId: stationId.toUpperCase(),
            project_name: stationName.toUpperCase(),
            checklist,
        };

        dispatch({
            type: CREATE_CHECKLIST,
            payload,
            cb: (res) => {
                if (res.status === 200) {
                    navigate(`/formList/${payload.stationId}`);
                }
            },
        });
    }, [validate, quantities, stationId, stationName, dispatch]);

    return (
        <div className="page-body">
            <Form>
                {/* Station ID */}
                <Form.Group className="mb-4" controlId="stationId">
                    <Form.Label className="fw-semibold">Station ID</Form.Label>
                    <Form.Control
                        type="text"
                        placeholder="Enter Station ID"
                        value={stationId}
                        onChange={(e) => {
                            const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
                            setStationId(value);
                            if (errors.stationId) setErrors((prev) => ({ ...prev, stationId: '' }));
                        }}
                        isInvalid={!!errors.stationId}
                    />
                    <Form.Control.Feedback type="invalid">{errors.stationId}</Form.Control.Feedback>
                </Form.Group>

                {/* Station Name */}
                <Form.Group className="mb-4" controlId="stationName">
                    <Form.Label className="fw-semibold">Station Name / Project Name</Form.Label>
                    <Form.Control
                        type="text"
                        placeholder="Enter Station Name / Project Name"
                        value={stationName}
                        onChange={(e) => {
                            const value = e.target.value.toUpperCase();
                            setStationName(value);
                            if (errors.stationName) setErrors((prev) => ({ ...prev, stationName: '' }));
                        }}
                        isInvalid={!!errors.stationName}
                    />
                    <Form.Control.Feedback type="invalid">{errors.stationName}</Form.Control.Feedback>
                </Form.Group>
                <h2>Select Number for Forms for {stationName ? stationName : ''} Project</h2>
                <br/>
                {/* Checklist Grid */}
                <div className="mb-4">
                    <Row>
                        {checklistItems.map((item) => (
                            <Col xs={6} md={4} lg={3} key={item} className="mb-4 text-center">
                                <div className="mb-2 text-primary">
                                    {checklistIcons[item] || <FaBoxes size={24} />}
                                </div>
                                <div className="fw-semibold mb-2">{item}</div>
                                <InputGroup className="justify-content-center">
                                    <Button
                                        variant="outline-secondary"
                                        onClick={() => adjustQuantity(item, -1)}
                                        disabled={quantities[item] === 0}
                                    >
                                        −
                                    </Button>
                                    <Form.Control
                                        type="number"
                                        value={quantities[item]}
                                        min={0}
                                        onChange={(e) => handleQuantityChange(item, e.target.value)}
                                        style={{
                                            textAlign: 'center',
                                            maxWidth: '60px',
                                        }}
                                    />
                                    <Button
                                        variant="outline-secondary"
                                        onClick={() => adjustQuantity(item, 1)}
                                    >
                                        +
                                    </Button>
                                </InputGroup>
                            </Col>
                        ))}
                    </Row>

                    {errors.checklist && (
                        <Alert variant="danger" className="mt-3">
                            {errors.checklist}
                        </Alert>
                    )}
                </div>

                <Button variant="success" onClick={handleSubmit}>
                    Create Site
                </Button>
            </Form>
        </div>
    );
};

export default FormsChecklist;
